import { useState } from 'react';
import { Key, Eye, EyeOff, LogOut, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

export default function ChangePasswordModal({ onPasswordChanged, onLogout }) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (newPassword.length < 6) {
      setError('La password deve essere di almeno 6 caratteri');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Le password non coincidono');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/auth/change-password`,
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Password cambiata con successo!');
      onPasswordChanged();
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore durante il cambio password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-8 card-shadow">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-[#5D1822]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Key className="w-8 h-8 text-[#5D1822]" />
          </div>
          <h2 className="text-2xl font-serif font-bold text-[#5D1822] mb-2">
            Cambia la tua Password
          </h2>
          <p className="text-[#5A5A5A] text-sm">
            Per motivi di sicurezza, devi impostare una nuova password personale prima di continuare.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* New Password */}
          <div>
            <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
              Nuova Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="input-default w-full pr-10"
                placeholder="Minimo 6 caratteri"
                required
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5A5A5A] hover:text-[#5D1822]"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-medium text-[#2C2C2C] mb-2">
              Conferma Password
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input-default w-full pr-10"
                placeholder="Ripeti la password"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5A5A5A] hover:text-[#5D1822]"
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full flex items-center justify-center gap-2"
          >
            {loading ? (
              'Salvataggio...'
            ) : (
              <>
                <Key size={18} />
                Salva Nuova Password
              </>
            )}
          </button>
        </form>

        {/* Logout Option */}
        <div className="mt-6 pt-4 border-t border-[#E5E5E5]">
          <button
            onClick={onLogout}
            className="w-full flex items-center justify-center gap-2 text-[#5A5A5A] hover:text-[#B94A48] transition-colors py-2"
          >
            <LogOut size={18} />
            <span className="text-sm">Esci e accedi con un altro account</span>
          </button>
        </div>
      </div>
    </div>
  );
}
